import React, { useMemo } from "react";
import { useFormState } from "react-final-form";
import {
  getExitStrategyOptions,
  isExitStrategyHidden,
} from "../../../constants/selectOptions/selectOptionsExitStrategy";
import normalizeOptions from "../../../utils/finalForm/normalizeOptions";
import RffSelectToggleField from "./RffSelectToggleField";

/**
 * Reads the live `loanProductType` and the field's current value, and returns
 * product-scoped exit-strategy options (with any out-of-list existing value
 * appended) plus whether the field applies at all.
 */
export const useExitStrategyOptions = (name = "exitStrategy") => {
  const { values } = useFormState({ subscription: { values: true } });
  const productType = values?.loanProductType;
  const currentValue = values?.[name];

  const options = useMemo(
    () => getExitStrategyOptions(productType, currentValue),
    [productType, currentValue],
  );

  return { options, hidden: isExitStrategyHidden(productType) };
};

/**
 * RffExitStrategyField — Exit Strategy select whose options depend on the loan's
 * product type. Renders nothing for products where an exit strategy doesn't
 * apply (DSCR / Other), so the question never appears where it makes no sense.
 */
const RffExitStrategyField = ({
  name = "exitStrategy",
  label = "Exit Strategy",
  ...rest
}) => {
  const { options, hidden } = useExitStrategyOptions(name);

  // Two different reasons to render nothing, and BOTH are needed. `hidden`
  // covers the products that have no exit strategy by definition; the empty
  // check covers not having picked a product yet — an unknown product isn't in
  // the hidden list, so it falls through with an empty option set and would
  // otherwise draw a label above nothing at all. The field appears the moment a
  // product that has options is chosen.
  if (hidden || options.length === 0) return null;

  return (
    <RffSelectToggleField
      name={name}
      label={label}
      options={normalizeOptions(options)}
      {...rest}
    />
  );
};

export default RffExitStrategyField;
